// A simplified view of the onboarding checklist for the chat: the core
// steps a new admin walks through, one line each, aggregated across
// locations. Automation steps (backfills, forecasts, pipeline) are left out
// on purpose; they run on their own and Lumi reports on them when asked.

const SIMPLE_STEPS = [
  ["business_type_selected", "Business type"],
  ["starting_modules_selected", "Modules"],
  ["company_pos_connected", "POS"],
  ["real_location_added", "Location"],
  ["location_settings_reviewed", "Address"],
  ["labor_law_settings_reviewed", "Labor laws"],
  ["hours_of_operation_reviewed", "Hours"],
  ["departments_defined", "Departments"],
  ["roles_reviewed", "Roles"],
  ["employees_added", "Team"],
  ["welcome_message_sent", "Invites"],
  ["training_content_started", "Training"],
  ["billing_reviewed", "Billing"],
];

// Done statuses: complete, or not part of this company's starting modules.
const DONE_STATUSES = new Set(["complete", "not_applicable"]);

function isSkipped(task) {
  const undo = task?.undoAction;
  return undo?.action === "undo" && String(undo.label || "").trim().toLowerCase() === "undo skip";
}

function allTasks(summary) {
  const company = Array.isArray(summary?.company?.tasks) ? summary.company.tasks : [];
  const stores = (Array.isArray(summary?.storeSummaries) ? summary.storeSummaries : []).flatMap((store) =>
    Array.isArray(store?.tasks) ? store.tasks : []
  );
  // Rightwork-internal steps are not the customer's to do.
  return [...company, ...stores].filter(
    (task) => task && task.key && (task.audience || "customer") !== "internal"
  );
}

// One item per simplified step: done (all matching tasks complete and none
// skipped), skipped, next (the checklist's next best action), or todo.
export function buildChecklistItems(summary) {
  const tasks = allTasks(summary);
  if (tasks.length === 0) return [];
  const nextKey = summary?.nextBestAction?.key || summary?.nextBestAction?.task_key || null;
  const items = [];
  for (const [key, label] of SIMPLE_STEPS) {
    const matching = tasks.filter((task) => task.key === key);
    if (matching.length === 0) continue;
    // A step that does not apply to this company's starting modules is
    // not on the list at all.
    if (matching.every((task) => task.status === "not_applicable" || task.moduleOff)) continue;
    let status = "todo";
    if (matching.every((task) => DONE_STATUSES.has(task.status))) {
      status = matching.some(isSkipped) ? "skipped" : "done";
    } else if (key === nextKey) {
      status = "next";
    }
    items.push({ key, label, status });
  }
  return items;
}

// Step keys that became done or skipped between two summaries.
// "Show me the checklist" is answered in the panel with the checklist card
// itself: no tool call, no navigation, no model guesswork.
const SHOW_CHECKLIST_RE =
  /\b(show|see|view|open|display|pull up|bring up|where('?s| is)|what('?s| is) on)\b[^?.!]{0,30}\bchecklist\b|^\s*checklist\s*[?.!]?\s*$/i;

export function isChecklistRequest(text) {
  const value = String(text || "").trim();
  if (!value || value.length > 60) return false;
  if (/\bexplain\b|\bwhat does\b|\bhow does\b/i.test(value)) return false;
  if (!SHOW_CHECKLIST_RE.test(value)) return false;
  // Only a request that is nothing but "show me the checklist": anything
  // else in the message has to reach Lumi.
  const rest = value
    .replace(SHOW_CHECKLIST_RE, " ")
    .replace(/\b(can|could|would|will|you|please|me|my|us|the|a|now|again|current|setup|onboarding)\b/gi, " ")
    .replace(/[^a-z0-9]+/gi, " ")
    .trim();
  return rest.length === 0;
}

export function completedBetween(before, after) {
  const was = new Map(buildChecklistItems(before).map((item) => [item.key, item.status]));
  return buildChecklistItems(after)
    .filter((item) => (item.status === "done" || item.status === "skipped") && was.get(item.key) !== item.status)
    .map((item) => item.key);
}
